'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { authStorage } from '@/shared/libs/authStorage';
import { Capabilities, ProfileType } from '@/shared/types/capabilities';
import { InternalProfile } from '@/shared/types/internal-profile';

type User = {
  id: string;
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  /** Nom de l'organisation. Source à préférer partout où l'on nomme la société. */
  organizationName?: string;
  /**
   * @deprecated Libellé recopié sur le compte, qui se désynchronise dès qu'une
   * organisation est renommée (suivora-api#454). Ne subsiste qu'en repli.
   */
  companyName?: string;
  /** Logo de l'interface connectée (branding org), fourni au login par le BE. */
  appLogoUrl?: string;
  consultantId?: string | null;
  /**
   * **Fonction occupée** dans l'organisation — « Assistante de direction », « Développeuse
   * Fullstack » (#1805).
   *
   * Distincte du **rôle**, qui dit à quoi le compte a accès, et distincte de `position`, qui est le
   * poste tenu **en mission** chez un client. Les confondre afficherait « Développeur Fullstack
   * chez BNP » à un gestionnaire de paie.
   *
   * Servie par `/auth/me` à partir de suivora-api#1356 ; absente d'ici là, et l'écran n'invente
   * rien.
   */
  jobTitle?: string | null;
  avatarUrl?: string;
  lastLogin?: string;
  roles?: string[];
  /** Permissions effectives (union des rôles), résolues par le BE — RBAC par org. Absent tant que le BE ne l'expose pas. */
  permissions?: string[];
  /** Organisation (tenant) du compte, exposée par `/auth/me` (RBAC / multi-tenant). */
  orgId?: string;
  profileType?: ProfileType;
  capabilities?: Capabilities;
  contractStart?: string | null;
  contractEnd?: string | null;
  internal?: InternalProfile | null;
};

type AuthState = {
  user: User | null;
  accessToken: string | null;
  isInitializing: boolean;
  isAuthenticated: boolean;
  setUser: (u: User | null) => void;
  setAccessToken: (t: string | null) => void;
  setInitializing: (v: boolean) => void;
  clearSession: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      user: null,
      accessToken: authStorage.getAccessToken(),
      isInitializing: true,
      isAuthenticated: false,

      setUser: user => {
        set(state => {
          const next = { ...state, user };
          next.isAuthenticated = !!(next.user && next.accessToken);
          return next;
        });
      },

      setAccessToken: token => {
        set(state => {
          if (token) {
            authStorage.setAccessToken(token);
          } else {
            authStorage.clearTokens();
          }
          const next = { ...state, accessToken: token ?? null };
          next.isAuthenticated = !!(next.user && next.accessToken);
          return next;
        });
      },

      setInitializing: v => set({ isInitializing: v }),

      clearSession: () => {
        authStorage.clearTokens();
        set({ user: null, accessToken: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-store',
      partialize: s => ({
        user: s.user,
        accessToken: s.accessToken,
        isAuthenticated: s.isAuthenticated,
      }),
    },
  ),
);
